import { PCCase } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Query parameters for filtering
export interface PCCaseQueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sizeCategories?: string[];
  formFactors?: string[];
  manufacturers?: string[];
  sidePanelTypes?: string[];
  minGpuLength?: number;
  minCoolerHeight?: number;
  minPsuLength?: number;
  minRadiatorSize?: number;
  sortBy?: 'name' | 'releaseYear' | 'manufacturer';
  sortOrder?: 'asc' | 'desc';
  allSet?: boolean;
}

export interface PCCaseQueryResult {
  data: PCCase[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Build query string from params
function buildQueryString(params: PCCaseQueryParams): string {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  if (params.minGpuLength) searchParams.set('minGpuLength', params.minGpuLength.toString());
  if (params.minCoolerHeight) searchParams.set('minCoolerHeight', params.minCoolerHeight.toString());
  if (params.minPsuLength) searchParams.set('minPsuLength', params.minPsuLength.toString());
  if (params.minRadiatorSize) searchParams.set('minRadiatorSize', params.minRadiatorSize.toString());

  if (params.sizeCategories?.length) searchParams.set('sizeCategories', params.sizeCategories.join(','));
  if (params.formFactors?.length) searchParams.set('formFactors', params.formFactors.join(','));
  if (params.manufacturers?.length) searchParams.set('manufacturers', params.manufacturers.join(','));
  if (params.sidePanelTypes?.length) searchParams.set('sidePanelTypes', params.sidePanelTypes.join(','));
  if (params.allSet !== undefined) searchParams.set('allSet', params.allSet.toString());

  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

// Fetch cases with server-side filtering and pagination
export const fetchCases = async (params: PCCaseQueryParams = {}): Promise<PCCaseQueryResult> => {
  const queryString = buildQueryString(params);
  const response = await fetch(`${API_BASE_URL}/api/cases${queryString}`);

  if (!response.ok) {
    throw new Error('Failed to fetch cases from API');
  }

  return response.json();
};

// Fetch a single case by ID
export const fetchCaseById = async (id: string): Promise<PCCase | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/cases/${id}`);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch case');
    }
    return response.json();
  } catch (error) {
    console.error('Failed to fetch case:', error);
    return null;
  }
};

// Fetch multiple cases by IDs
export const fetchCasesByIds = async (ids: string[]): Promise<PCCase[]> => {
  if (ids.length === 0) return [];

  try {
    const response = await fetch(`${API_BASE_URL}/api/cases/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids }),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cases');
    }
    return response.json();
  } catch (error) {
    console.error('Failed to fetch cases by IDs:', error);
    return [];
  }
};

// Fetch all unique manufacturers
export const fetchManufacturers = async (): Promise<string[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/filters`);
    if (!response.ok) {
      throw new Error('Failed to fetch filters');
    }
    const data = await response.json();
    return data.manufacturers;
  } catch (error) {
    console.error('Failed to fetch manufacturers:', error);
    return [];
  }
};

export type StatsQueryParams = Pick<
  PCCaseQueryParams,
  'search' | 'formFactors' | 'manufacturers' | 'sidePanelTypes' | 'minGpuLength' | 'minCoolerHeight' | 'minPsuLength'
>;

// Fetch case count stats
export const fetchCaseStats = async (params?: StatsQueryParams): Promise<{
  total: number;
  byCategory: Record<string, number>;
}> => {
  try {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.formFactors?.length) searchParams.set('formFactors', params.formFactors.join(','));
    if (params?.manufacturers?.length) searchParams.set('manufacturers', params.manufacturers.join(','));
    if (params?.sidePanelTypes?.length) searchParams.set('sidePanelTypes', params.sidePanelTypes.join(','));
    if (params?.minGpuLength) searchParams.set('minGpuLength', params.minGpuLength.toString());
    if (params?.minCoolerHeight) searchParams.set('minCoolerHeight', params.minCoolerHeight.toString());
    if (params?.minPsuLength) searchParams.set('minPsuLength', params.minPsuLength.toString());
    const qs = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/api/stats${qs ? `?${qs}` : ''}`);
    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    return response.json();
  } catch (error) {
    console.error('Failed to fetch stats:', error);
    return { total: 0, byCategory: {} };
  }
};

// Create a new case
export const createCase = async (caseData: Omit<PCCase, 'id' | 'createdAt' | 'updatedAt'>): Promise<PCCase> => {
  const response = await fetch(`${API_BASE_URL}/api/cases`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(caseData),
  });
  if (!response.ok) throw new Error('Failed to create case');
  return response.json();
};

// Update a case
export const updateCase = async (id: string, caseData: Partial<PCCase>): Promise<PCCase> => {
  const response = await fetch(`${API_BASE_URL}/api/cases/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(caseData),
  });
  if (!response.ok) throw new Error('Failed to update case');
  return response.json();
};

// Delete a case
export const deleteCase = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/cases/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete case');
};
